import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { Link } from '@tanstack/react-router';
import Fuse, { type FuseResult } from 'fuse.js';
import * as Sentry from '@sentry/react';
import type { SearchItem } from '@/shared/types/api.types';

function HighlightedText({
  text,
  matches,
  matchKey,
}: {
  text: string;
  matches: FuseResult<SearchItem>['matches'];
  matchKey: string;
}): React.ReactElement {
  if (!matches || !text) return <>{text}</>;
  const match = matches.find(m => m.key === matchKey);
  if (!match || match.indices.length === 0) return <>{text}</>;

  const sorted = [...match.indices].sort((a, b) => a[0] - b[0]);
  const segments: React.ReactNode[] = [];
  let cursor = 0;

  sorted.forEach(([start, end], i) => {
    if (start < cursor) return;
    if (start > cursor)
      segments.push(<Fragment key={`t-${i}`}>{text.substring(cursor, start)}</Fragment>);
    segments.push(
      <mark
        key={`m-${i}`}
        className="bg-warning/30 text-warning-foreground rounded px-1 font-medium"
      >
        {text.substring(start, end + 1)}
      </mark>
    );
    cursor = end + 1;
  });
  if (cursor < text.length) segments.push(<Fragment key="tail">{text.substring(cursor)}</Fragment>);
  return <>{segments}</>;
}

export function SearchBox(): React.ReactElement {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<FuseResult<SearchItem>[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const fuseRef = useRef<Fuse<SearchItem> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    async function loadSearchData(): Promise<void> {
      try {
        const response = await fetch('/search-index.json');
        const data = (await response.json()) as SearchItem[];

        fuseRef.current = new Fuse(data, {
          keys: [
            { name: 'title', weight: 0.4 },
            { name: 'content', weight: 0.3 },
            { name: 'section', weight: 0.2 },
            { name: 'keywords', weight: 0.1 },
          ],
          threshold: 0.3,
          includeScore: true,
          includeMatches: true,
          minMatchCharLength: 2,
          ignoreLocation: true,
        });
      } catch (error) {
        Sentry.captureException(error);
      }
    }

    loadSearchData();
  }, []);

  const performSearch = useCallback((searchQuery: string) => {
    if (!fuseRef.current || searchQuery.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    const searchResults = fuseRef.current.search(searchQuery);
    setResults(searchResults.slice(0, 8));
    setIsOpen(true);
    setIsLoading(false);
  }, []);

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const value = event.target.value;
    setQuery(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      performSearch(value.trim());
    }, 300);
  }

  function handleKeyDown(event: React.KeyboardEvent): void {
    if (event.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="search-container relative z-100000">
      <input
        ref={inputRef}
        type="text"
        placeholder="Buscar en APEPS..."
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => query.length >= 2 && setIsOpen(true)}
        className="border-border/50 bg-card/30 text-foreground shadow-strong placeholder:text-muted-foreground focus:bg-card/50 focus:ring-primary/50 w-64 transform rounded-xl border py-2 pr-3 pl-10 text-xs backdrop-blur-xl transition-all duration-700 ease-out outline-none placeholder:text-xs hover:-translate-y-1 hover:scale-105 focus:-translate-y-1 focus:scale-105 focus:border-transparent focus:ring-2 focus:backdrop-blur-2xl"
        autoComplete="off"
        role="combobox"
        aria-expanded={isOpen}
        aria-controls="search-results"
        aria-autocomplete="list"
        aria-label="Buscar en APEPS"
      />
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center pl-3">
        <svg
          className="text-muted-foreground h-4 w-4 transition-all duration-500 ease-out"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>

      {isLoading && (
        <div className="absolute inset-y-0 right-0 z-10 flex items-center pr-3">
          <div className="border-primary h-3.5 w-3.5 animate-spin rounded-full border-2 border-t-transparent" />
        </div>
      )}

      {isOpen && (
        <div
          id="search-results"
          role="listbox"
          aria-live="polite"
          className="border-border/20 bg-card/95 shadow-strong absolute top-full left-0 z-100001 mt-1 max-h-96 w-full overflow-y-auto rounded-2xl border backdrop-blur-xl"
        >
          {results.length === 0 ? (
            <div className="p-6 text-center">
              <div className="text-muted-foreground mb-2">
                <svg
                  className="mx-auto mb-3 h-12 w-12 opacity-50"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0121 12c0-4.418-3.582-8-8-8s-8 3.582-8 8c0 2.152.851 4.103 2.233 5.708L3 22l5.708-4.267z"
                  />
                </svg>
              </div>
              <p className="text-muted-foreground">
                No se encontraron resultados para &quot;<strong>{query}</strong>&quot;
              </p>
              <p className="text-muted-foreground mt-1 text-sm">
                Intenta con otros términos de búsqueda
              </p>
            </div>
          ) : (
            <div className="p-2">
              <div className="border-border/50 flex items-center justify-between border-b p-3">
                <span className="text-foreground text-sm font-semibold">
                  Resultados de búsqueda
                </span>
                <span className="text-muted-foreground text-xs">
                  {results.length} resultado{results.length !== 1 ? 's' : ''}
                </span>
              </div>
              {results.map((result, index) => {
                const score = Math.round((1 - (result.score ?? 0)) * 100);
                return (
                  <Link
                    key={index}
                    to={result.item.url}
                    onClick={() => setIsOpen(false)}
                    role="option"
                    className="group border-border/50 hover:bg-accent/50 block border-b p-4 transition-colors duration-200 last:border-b-0"
                  >
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <h4 className="text-foreground group-hover:text-primary truncate text-sm font-semibold transition-colors duration-200">
                          <HighlightedText
                            text={result.item.title}
                            matches={result.matches}
                            matchKey="title"
                          />
                        </h4>
                        <p className="text-muted-foreground mt-1 line-clamp-2 text-xs">
                          <HighlightedText
                            text={result.item.content.substring(0, 100) + '...'}
                            matches={result.matches}
                            matchKey="content"
                          />
                        </p>
                        <div className="mt-2 flex items-center space-x-2">
                          <span className="bg-primary/15 text-primary inline-flex items-center rounded-full px-2 py-1 text-xs font-medium">
                            {result.item.section}
                          </span>
                          <span className="text-muted-foreground text-xs">{score}% relevancia</span>
                        </div>
                      </div>
                      <svg
                        className="text-muted-foreground group-hover:text-primary ml-2 h-4 w-4 shrink-0 transition-colors duration-200"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
